import Chart from 'chart.js';
import Hls, {
  BufferCreatedTrackSet,
  FragChangedData,
  FragLoadedData,
  FragParsedData,
  Level,
  LevelDetails,
  MediaPlaylist,
} from 'hls.js';
import { applyChartInstanceOverrides, hhmmss } from './chartjs-horizontal-bar';

const X_AXIS_SECONDS = 'x-axis-seconds';

interface ChartScale {
  width: number;
  height: number;
  min: number;
  max: number;
  options: { ticks: { min: number; max: number } };
  getPixelForValue: (value: number) => number;
  getValueForPixel: (pixel: number) => number;
}

type TimelineDataset = Record<string, unknown> & {
  data: Record<string, unknown>[];
  url?: string;
  level?: number;
  trackType?: string;
  barThickness: number;
};

/**
 * Multi-row Gantt chart for HLS playlists, fragments, and buffer ranges.
 * Ported from the hls.js demo timeline tab; receives an Hls instance instead of using globals.
 */
export class TimelineChart {
  private readonly chart: Chart;
  private readonly hls: Hls;
  private updateRafRequestId: number = -1;
  private resizeRafRequestId: number = -1;
  private imageDataBuffer: ImageData | null = null;
  private media: HTMLMediaElement | null = null;
  private tracksChangeHandler?: (e: Event) => void;
  private cuesChangeHandler?: (e: Event) => void;
  private hidden: boolean = true;
  private zoom100: number = 60;

  constructor(canvas: HTMLCanvasElement, hls: Hls, chartJsOptions?: Record<string, unknown>) {
    this.hls = hls;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error(`Could not get CanvasRenderingContext2D from canvas: ${canvas}`);
    }

    this.chart = new Chart(ctx, {
      type: 'horizontalBar',
      data: {
        labels: [],
        datasets: [],
      },
      options: Object.assign(getChartOptions(), chartJsOptions) as unknown as Chart.ChartOptions,
      plugins: [
        {
          afterRender: () => {
            this.imageDataBuffer = null;
            // afterRender can fire during `new Chart()` before `this.chart` is assigned
            if (!this.chart?.data) {
              return;
            }
            this.drawCurrentTime();
          },
        },
      ],
    });

    applyChartInstanceOverrides(this.chart);
    this.bindCanvasInteractions(canvas);
  }

  /** Removes the chart and clears canvas interaction handlers. */
  destroy() {
    cancelAnimationFrame(this.updateRafRequestId);
    cancelAnimationFrame(this.resizeRafRequestId);
    this.chart.destroy();
  }

  private bindCanvasInteractions(canvas: HTMLCanvasElement) {
    canvas.ondblclick = (event: MouseEvent) => {
      const chartArea = this.chart.chartArea;
      const element = this.chart.getElementAtEvent(event);
      const pos = Chart.helpers.getRelativePosition(event, this.chart);
      const scale = this.chartScales[X_AXIS_SECONDS];
      if (element.length || pos.x > chartArea.left) {
        const amount = event.getModifierState('Shift') ? -1.0 : 0.5;
        this.zoom(scale, pos, amount);
      } else {
        scale.options.ticks.min = 0;
        scale.options.ticks.max = this.zoom100;
      }
      this.update();
    };

    canvas.onwheel = (event: WheelEvent) => {
      if (event.deltaMode) {
        return;
      }
      const chartArea = this.chart.chartArea;
      const pos = Chart.helpers.getRelativePosition(event, this.chart);
      if (pos.x > chartArea.left - 11) {
        const scale = this.chartScales[X_AXIS_SECONDS];
        if (event.deltaY) {
          const direction = -event.deltaY / Math.abs(event.deltaY);
          const normal = Math.min(333, Math.abs(event.deltaY)) / 1000;
          const ease = 1 - (1 - normal) * (1 - normal);
          this.zoom(scale, pos, ease * direction);
        } else if (event.deltaX) {
          this.pan(scale, event.deltaX / 10, scale.min, scale.max);
        }
        event.preventDefault();
      }
    };

    let moved = false;
    let gestureScale = 1;

    canvas.onpointerdown = (downEvent: PointerEvent) => {
      if (!downEvent.isPrimary || gestureScale !== 1) {
        return;
      }
      const chartArea = this.chart.chartArea;
      const pos = Chart.helpers.getRelativePosition(downEvent, this.chart);
      if (pos.x > chartArea.left) {
        const scale = this.chartScales[X_AXIS_SECONDS];
        const startX = downEvent.clientX;
        const { min, max } = scale;
        const xToVal = (max - min) / scale.width;
        moved = false;
        canvas.setPointerCapture(downEvent.pointerId);
        canvas.onpointermove = (moveEvent: PointerEvent) => {
          if (!downEvent.isPrimary || gestureScale !== 1) {
            return;
          }
          const movedX = startX - moveEvent.clientX;
          const movedValue = movedX * xToVal;
          moved = moved || Math.abs(movedX) > 8;
          this.pan(scale, movedValue, min, max);
        };
      }
    };

    canvas.onpointerup = canvas.onpointercancel = (upEvent: PointerEvent) => {
      if (canvas.onpointermove) {
        canvas.onpointermove = null;
        canvas.releasePointerCapture(upEvent.pointerId);
      }
      if (!moved && upEvent.isPrimary) {
        this.click(upEvent);
      }
    };

    const gestureCanvas = canvas as HTMLCanvasElement & {
      ongesturestart: ((event: Event) => void) | null;
      ongestureend: ((event: Event) => void) | null;
      ongesturechange: ((event: Event & { scale?: number }) => void) | null;
    };

    gestureCanvas.ongesturestart = (event: Event) => {
      gestureScale = 1;
      event.preventDefault();
    };

    gestureCanvas.ongestureend = () => {
      gestureScale = 1;
    };

    gestureCanvas.ongesturechange = (event: Event & { scale?: number }) => {
      const chartArea = this.chart.chartArea;
      const pos = Chart.helpers.getRelativePosition(event as unknown as MouseEvent, this.chart);
      if (pos.x > chartArea.left && event.scale != null) {
        const scale = this.chartScales[X_AXIS_SECONDS];
        const amount = event.scale - gestureScale;
        this.zoom(scale, pos, amount);
        gestureScale = event.scale;
      }
    };
  }

  private click(event: MouseEvent) {
    const element = this.chart.getElementAtEvent(event);
    const datasets = this.chart.data.datasets as TimelineDataset[] | undefined;
    if (element.length && datasets) {
      const dataset = datasets[(element[0] as { _datasetIndex: number })._datasetIndex];
      const obj = dataset.data[(element[0] as { _index: number })._index];
      if (this.hls.media) {
        const scale = this.chartScales[X_AXIS_SECONDS];
        const pos = Chart.helpers.getRelativePosition(event, this.chart);
        this.hls.media.currentTime = scale.getValueForPixel(pos.x);
      }
      if (process.env.NODE_ENV === 'development') {
        console.log(obj);
      }
    }
  }

  private pan(scale: ChartScale, amount: number, min: number, max: number) {
    if (amount === 0) {
      return;
    }
    let pan = amount;
    if (amount > 0) {
      pan = Math.min(this.zoom100 + 10 - max, amount);
    } else {
      pan = Math.max(-10 - min, amount);
    }
    scale.options.ticks.min = min + pan;
    scale.options.ticks.max = max + pan;
    this.updateOnRepaint();
  }

  private zoom(scale: ChartScale, pos: { x: number }, amount: number) {
    const range = scale.max - scale.min;
    const diff = range * amount;
    const minPercent = (scale.getValueForPixel(pos.x) - scale.min) / range;
    const maxPercent = 1 - minPercent;
    const minDelta = diff * minPercent;
    const maxDelta = diff * maxPercent;
    scale.options.ticks.min = Math.max(-10, scale.min + minDelta);
    scale.options.ticks.max = Math.min(this.zoom100 + 10, scale.max - maxDelta);
    this.updateOnRepaint();
  }

  get chartScales(): Record<string, ChartScale> {
    return (this.chart as unknown as { scales: Record<string, ChartScale> }).scales;
  }

  /** Resets the time-axis zoom to the full loaded range (double-click left area in the demo). */
  resetZoom() {
    const scale = this.chartScales[X_AXIS_SECONDS];
    if (!scale) {
      return;
    }
    scale.options.ticks.min = 0;
    scale.options.ticks.max = this.zoom100;
    this.update();
  }

  reset() {
    const scale = this.chartScales[X_AXIS_SECONDS];
    scale.options.ticks.min = 0;
    scale.options.ticks.max = 60;
    const { labels, datasets } = this.chart.data;
    if (labels && datasets) {
      labels.length = 0;
      datasets.length = 0;
      this.resize(datasets as TimelineDataset[]);
    }
  }

  update() {
    if (this.hidden || !this.chart.ctx?.canvas.width) {
      return;
    }
    cancelAnimationFrame(this.updateRafRequestId);
    this.chart.update({ duration: 0, lazy: true });
  }

  updateOnRepaint() {
    if (this.hidden) {
      return;
    }
    cancelAnimationFrame(this.updateRafRequestId);
    this.updateRafRequestId = requestAnimationFrame(() => this.update());
  }

  /** Sets canvas container height from row count and expands if Chart.js plot area is too short. */
  private fitContainerToRows(datasets: TimelineDataset[]) {
    const scale = this.chartScales[X_AXIS_SECONDS];
    const rowsHeight = datasets.reduce((val, dataset) => val + dataset.barThickness, 0);
    const xAxisHeight = scale?.height ?? 40;
    const { top, bottom } = this.chart.chartArea;
    const topPadding = top > 0 && bottom < 500 ? top : 10;
    let height = topPadding + rowsHeight + xAxisHeight + 10;
    const container = this.chart.canvas?.parentElement;
    if (container) {
      container.style.height = `${height}px`;
    }

    this.chart.resize();

    const plotHeight = this.chart.chartArea.bottom - this.chart.chartArea.top;
    const plotTooShort = plotHeight < rowsHeight;

    if (plotTooShort) {
      const deficit = rowsHeight - plotHeight;
      height = (container?.clientHeight ?? height) + deficit + 10;
      if (container) {
        container.style.height = `${height}px`;
      }
      this.chart.resize();
    }
  }

  resize(datasets?: TimelineDataset[]) {
    if (this.hidden) {
      return;
    }

    cancelAnimationFrame(this.resizeRafRequestId);
    this.resizeRafRequestId = requestAnimationFrame(() => {
      if (datasets?.length) {
        this.fitContainerToRows(datasets);
      } else {
        this.chart.resize();
      }
    });
  }

  show() {
    this.hidden = false;
  }

  hide() {
    this.hidden = true;
  }

  updateLevels(levels: Level[], levelSwitched?: number) {
    const { labels, datasets } = this.chart.data;
    if (!labels || !datasets) {
      return;
    }
    const { loadLevel, nextLoadLevel, nextAutoLevel, currentLevel } = this.hls;
    const activeLevel = levelSwitched !== undefined ? levelSwitched : currentLevel;

    levels.forEach((level, i) => {
      const index = level.id || i;
      labels.push(getLevelName(level, index));
      let borderColor: string | null = null;
      if (activeLevel === i) {
        borderColor = 'rgba(32, 32, 240, 1.0)';
      } else if (loadLevel === i) {
        borderColor = 'rgba(255, 128, 0, 1.0)';
      } else if (nextLoadLevel === i) {
        borderColor = 'rgba(200, 200, 64, 1.0)';
      } else if (nextAutoLevel === i) {
        borderColor = 'rgba(160, 0, 160, 1.0)';
      }
      (datasets as TimelineDataset[]).push(
        datasetWithDefaults({
          url: Array.isArray(level.url) ? level.url[level.urlId || 0] : level.url,
          trackType: 'level',
          borderColor,
          level: index,
        }),
      );
      if (level.details) {
        this.updateLevelOrTrack(level.details);
      }
    });
    this.resize(datasets as TimelineDataset[]);
  }

  updateAudioTracks(audioTracks: MediaPlaylist[]) {
    const { labels, datasets } = this.chart.data;
    if (!labels || !datasets) {
      return;
    }
    const { audioTrack } = this.hls;
    audioTracks.forEach((track, i) => {
      labels.push(getAudioTrackName(track, i));
      (datasets as TimelineDataset[]).push(
        datasetWithDefaults({
          url: track.url,
          trackType: 'audioTrack',
          borderColor: audioTrack === i ? 'rgba(32, 32, 240, 1.0)' : null,
          audioTrack: i,
        }),
      );
      if (track.details) {
        this.updateLevelOrTrack(track.details);
      }
    });
    this.resize(datasets as TimelineDataset[]);
  }

  updateSubtitleTracks(subtitles: MediaPlaylist[]) {
    const { labels, datasets } = this.chart.data;
    if (!labels || !datasets) {
      return;
    }
    const { subtitleTrack } = this.hls;
    subtitles.forEach((track, i) => {
      labels.push(getSubtitlesName(track, i));
      (datasets as TimelineDataset[]).push(
        datasetWithDefaults({
          url: track.url,
          trackType: 'subtitleTrack',
          borderColor: subtitleTrack === i ? 'rgba(32, 32, 240, 1.0)' : null,
          subtitleTrack: i,
        }),
      );
      if (track.details) {
        this.updateLevelOrTrack(track.details);
      }
    });
    this.resize(datasets as TimelineDataset[]);
  }

  removeType(trackType: 'level' | 'audioTrack' | 'subtitleTrack' | 'textTrack') {
    const { labels, datasets } = this.chart.data;
    if (!labels || !datasets) {
      return;
    }
    let i = datasets.length;
    while (i--) {
      if ((datasets[i] as TimelineDataset).trackType === trackType) {
        datasets.splice(i, 1);
        labels.splice(i, 1);
      }
    }
  }

  updateLevelOrTrack(details: LevelDetails) {
    const { targetduration, totalduration, url } = details;
    const datasets = this.chart.data.datasets as TimelineDataset[];
    let levelDataSet = arrayFind(
      datasets,
      (dataset) => stripDeliveryDirectives(url) === stripDeliveryDirectives(dataset.url || ''),
    );
    if (!levelDataSet) {
      levelDataSet = arrayFind(
        datasets,
        (dataset) => details.fragments[0]?.level === dataset.level,
      );
    }
    if (!levelDataSet) {
      return;
    }

    const data = levelDataSet.data;
    data.length = 0;

    if (details.fragments) {
      details.fragments.forEach((fragment) => {
        data.push(
          Object.assign({ dataType: 'fragment' }, fragment, { loader: null }) as unknown as Record<
            string,
            unknown
          >,
        );
      });
    }

    if (details.partList) {
      details.partList.forEach((part) => {
        data.push(
          Object.assign({ dataType: 'part', start: part.fragment.start + part.fragOffset }, part, {
            fragment: Object.assign({}, part.fragment, { loader: null }),
          }) as unknown as Record<string, unknown>,
        );
      });
      if (details.fragmentHint) {
        data.push(
          Object.assign({ dataType: 'fragmentHint' }, details.fragmentHint, {
            loader: null,
          }) as unknown as Record<string, unknown>,
        );
      }
    }

    const start = getPlaylistStart(details);
    this.maxZoom = this.zoom100 = Math.max(
      start + totalduration + targetduration * 3,
      this.zoom100,
    );
    this.updateOnRepaint();
  }

  get minZoom(): number {
    const scale = this.chartScales[X_AXIS_SECONDS];
    return scale ? scale.options.ticks.min : 1;
  }

  get maxZoom(): number {
    const scale = this.chartScales[X_AXIS_SECONDS];
    return scale ? scale.options.ticks.max : this.zoom100;
  }

  set maxZoom(x: number) {
    const currentZoom = this.maxZoom;
    const newZoom = Math.max(x, currentZoom);
    if (currentZoom === 60 && newZoom !== currentZoom) {
      const scale = this.chartScales[X_AXIS_SECONDS];
      scale.options.ticks.max = newZoom;
    }
  }

  updateFragment(data: FragLoadedData | FragParsedData | FragChangedData) {
    const datasets = this.chart.data.datasets as TimelineDataset[];
    const frag = data.frag;
    let levelDataSet = arrayFind(datasets, (dataset) => frag.baseurl === dataset.url);
    if (!levelDataSet) {
      levelDataSet = arrayFind(datasets, (dataset) => frag.level === dataset.level);
    }
    if (!levelDataSet) {
      return;
    }
    const fragData = arrayFind(
      levelDataSet.data,
      (entry) => entry.relurl === frag.relurl && entry.sn === frag.sn,
    );
    if (fragData) {
      Object.assign(fragData, frag);
    }
    this.updateOnRepaint();
  }

  updateSourceBuffers(tracks: BufferCreatedTrackSet, media: HTMLMediaElement) {
    const { labels, datasets } = this.chart.data;
    if (!labels || !datasets) {
      return;
    }

    const trackTypes = Object.keys(tracks).sort((type) => (type === 'video' ? 1 : -1));
    const mediaBufferData: number[][] = [];

    this.removeSourceBuffers();
    this.media = media;

    trackTypes.forEach((type) => {
      const track = tracks[type as keyof BufferCreatedTrackSet];
      if (!track) {
        return;
      }
      const data: number[][] = [];
      const sourceBuffer = track.buffer;
      const backgroundColor =
        {
          video: 'rgba(0, 0, 255, 0.2)',
          audio: 'rgba(128, 128, 0, 0.2)',
          audiovideo: 'rgba(128, 128, 255, 0.2)',
        }[type] ?? 'rgba(128, 128, 128, 0.2)';

      labels.unshift(`${type} buffer (${track.id})`);
      (datasets as TimelineDataset[]).unshift(
        datasetWithDefaults({
          data,
          categoryPercentage: 0.5,
          backgroundColor,
          sourceBuffer,
        }),
      );

      sourceBuffer.addEventListener('update', () => {
        try {
          replaceTimeRangeTuples(sourceBuffer.buffered, data);
        } catch (error) {
          console.warn(error);
          return;
        }
        replaceTimeRangeTuples(media.buffered, mediaBufferData);
        this.update();
      });
    });

    if (trackTypes.length === 0) {
      media.onprogress = () => {
        replaceTimeRangeTuples(media.buffered, mediaBufferData);
        this.update();
      };
    }

    labels.unshift('media buffer');
    (datasets as TimelineDataset[]).unshift(
      datasetWithDefaults({
        data: mediaBufferData,
        categoryPercentage: 0.5,
        backgroundColor: 'rgba(0, 255, 0, 0.2)',
        media,
      }),
    );

    media.ontimeupdate = () => this.drawCurrentTime();

    const { textTracks } = media;
    this.tracksChangeHandler =
      this.tracksChangeHandler || ((e) => this.setTextTracks(e.currentTarget as TextTrackList));
    textTracks.removeEventListener('addtrack', this.tracksChangeHandler);
    textTracks.removeEventListener('removetrack', this.tracksChangeHandler);
    textTracks.removeEventListener('change', this.tracksChangeHandler);
    textTracks.addEventListener('addtrack', this.tracksChangeHandler);
    textTracks.addEventListener('removetrack', this.tracksChangeHandler);
    textTracks.addEventListener('change', this.tracksChangeHandler);
    this.setTextTracks(textTracks);
    this.resize(datasets as TimelineDataset[]);
  }

  removeSourceBuffers() {
    const { labels, datasets } = this.chart.data;
    if (!labels || !datasets) {
      return;
    }
    let i = datasets.length;
    while (i--) {
      if ((labels[0] || '').toString().indexOf('buffer') > -1) {
        datasets.splice(i, 1);
        labels.splice(i, 1);
      }
    }
  }

  setTextTracks(textTracks: TextTrackList) {
    const { labels, datasets } = this.chart.data;
    if (!labels || !datasets) {
      return;
    }
    this.removeType('textTrack');

    Array.from(textTracks).forEach((textTrack, i) => {
      const data: Record<string, unknown>[] = [];
      labels.push(`${textTrack.label} ${textTrack.kind} (${textTrack.mode})`);
      (datasets as TimelineDataset[]).push(
        datasetWithDefaults({
          data,
          categoryPercentage: 0.5,
          url: '',
          trackType: 'textTrack',
          borderColor:
            (textTrack.mode !== 'hidden') === (i === 0) ? 'rgba(32, 32, 240, 1.0)' : null,
          textTrack: i,
        }),
      );
      this.cuesChangeHandler =
        this.cuesChangeHandler ||
        ((e) =>
          this.updateTextTrackCues(
            e.currentTarget as TextTrack & { _data?: Record<string, unknown>[] },
          ));
      (textTrack as TextTrack & { _data?: Record<string, unknown>[] })._data = data;
      textTrack.removeEventListener('cuechange', this.cuesChangeHandler);
      textTrack.addEventListener('cuechange', this.cuesChangeHandler);
      this.updateTextTrackCues(textTrack as TextTrack & { _data?: Record<string, unknown>[] });
    });
    this.resize(datasets as TimelineDataset[]);
  }

  updateTextTrackCues(textTrack: TextTrack & { _data?: Record<string, unknown>[] }) {
    const data = textTrack._data;
    if (!data) {
      return;
    }
    const { activeCues, cues } = textTrack;
    data.length = 0;
    if (!cues) {
      return;
    }

    let activeLength = 0;
    let activeMin = Infinity;
    let activeMax = 0;
    if (activeCues) {
      activeLength = activeCues.length;
      for (let i = 0; i < activeLength; i++) {
        const cue = activeCues[i];
        if (cue) {
          activeMin = Math.min(activeMin, cue.startTime);
          activeMax = cue.endTime ? Math.max(activeMax, cue.endTime) : activeMax;
        } else {
          activeLength--;
        }
      }
    }

    for (let i = 0; i < cues.length; i++) {
      const cue = cues[i];
      if (!cue) {
        continue;
      }
      const start = cue.startTime;
      const end = cue.endTime;
      const content = getCueLabel(cue);
      let active = false;
      if (activeLength && end >= activeMin && start <= activeMax && activeCues) {
        active = Array.from(activeCues).some((activeCue) => cuesMatch(activeCue, cue));
      }
      data.push({ start, end, content, active, dataType: 'cue' });
    }
    this.updateOnRepaint();
  }

  drawCurrentTime() {
    const chart = this.chart;
    if (!chart?.data) {
      return;
    }

    const media = this.hls.media;
    const datasets = chart.data.datasets as TimelineDataset[] | undefined;
    const firstDataset = datasets?.[0];
    if (!media || !firstDataset) {
      return;
    }

    const currentTime = media.currentTime;
    const scale = this.chartScales[X_AXIS_SECONDS];
    const ctx = chart.ctx;
    if (this.hidden || !ctx?.canvas.width) {
      return;
    }

    const chartArea = chart.chartArea;
    const x = scale.getPixelForValue(currentTime);
    ctx.restore();
    ctx.save();
    this.drawLineX(ctx, x, chartArea, media);
    if (x > chartArea.left && x < chartArea.right) {
      ctx.fillStyle = this.getCurrentTimeColor(media);
      const y = chartArea.top + firstDataset.barThickness + 1;
      ctx.fillText(hhmmss(currentTime, 5), x + 2, y, 100);
    }
    ctx.restore();
  }

  getCurrentTimeColor(video: HTMLMediaElement): string {
    if (!video.readyState || video.ended) {
      return 'rgba(0, 0, 0, 0.9)';
    }
    if (video.seeking || video.readyState < 3) {
      return 'rgba(255, 128, 0, 0.9)';
    }
    if (video.paused) {
      return 'rgba(128, 0, 255, 0.9)';
    }
    return 'rgba(0, 0, 255, 0.9)';
  }

  drawLineX(
    ctx: CanvasRenderingContext2D,
    x: number,
    chartArea: { left: number; right: number; top: number; bottom: number },
    media: HTMLMediaElement,
  ) {
    if (!this.imageDataBuffer) {
      const devicePixelRatio = window.devicePixelRatio || 1;
      this.imageDataBuffer = ctx.getImageData(
        0,
        0,
        chartArea.right * devicePixelRatio,
        chartArea.bottom * devicePixelRatio,
      );
    } else {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, chartArea.right, chartArea.bottom);
      ctx.putImageData(this.imageDataBuffer, 0, 0);
    }
    if (x > chartArea.left && x < chartArea.right) {
      ctx.lineWidth = 1;
      ctx.strokeStyle = this.getCurrentTimeColor(media);
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
    }
  }
}

function stripDeliveryDirectives(url: string): string {
  if (url === '') {
    return url;
  }
  try {
    const webUrl = new URL(url);
    webUrl.searchParams.delete('_HLS_msn');
    webUrl.searchParams.delete('_HLS_part');
    webUrl.searchParams.delete('_HLS_skip');
    webUrl.searchParams.sort();
    return webUrl.href;
  } catch {
    return url.replace(/[?&]_HLS_(?:msn|part|skip)=[^?&]+/g, '');
  }
}

function datasetWithDefaults(options: Record<string, unknown>): TimelineDataset {
  return Object.assign(
    {
      data: [],
      xAxisID: X_AXIS_SECONDS,
      barThickness: 35,
      categoryPercentage: 1,
    },
    options,
  ) as TimelineDataset;
}

function getPlaylistStart(details: LevelDetails): number {
  return details.fragments?.length ? details.fragments[0].start : 0;
}

function getLevelName(level: Level, index: number) {
  let label = '(main playlist)';
  if (level.attrs?.BANDWIDTH) {
    label = `${getMainLevelAttribute(level)}@${level.attrs.BANDWIDTH}`;
    if (level.name) {
      label = `${label} (${level.name})`;
    }
  } else if (level.name) {
    label = level.name;
  }
  return `${label} L-${index}`;
}

function getMainLevelAttribute(level: Level) {
  return level.attrs.RESOLUTION || level.attrs.CODECS || level.attrs.AUDIO;
}

function getAudioTrackName(track: MediaPlaylist, index: number) {
  const label = track.lang ? `${track.name}/${track.lang}` : track.name;
  return `${label} (${track.groupId || track.attrs['GROUP-ID']}) A-${index}`;
}

function getSubtitlesName(track: MediaPlaylist, index: number) {
  const label = track.lang ? `${track.name}/${track.lang}` : track.name;
  return `${label} (${track.groupId || track.attrs['GROUP-ID']}) S-${index}`;
}

function replaceTimeRangeTuples(timeRanges: TimeRanges, data: number[][]) {
  data.length = 0;
  for (let i = 0; i < timeRanges.length; i++) {
    data.push([timeRanges.start(i), timeRanges.end(i)]);
  }
}

function cuesMatch(cue1: TextTrackCue, cue2: TextTrackCue) {
  const text1 = getCueText(cue1);
  const text2 = getCueText(cue2);
  return cue1.startTime === cue2.startTime && cue1.endTime === cue2.endTime && text1 === text2;
}

function getCueText(cue: TextTrackCue) {
  return 'text' in cue ? String((cue as VTTCue).text) : '';
}

function getCueLabel(cue: TextTrackCue) {
  const text = getCueText(cue);
  if (text) {
    return text;
  }
  return JSON.stringify(parseDataCue(cue));
}

function parseDataCue(
  cue: TextTrackCue & { value?: { key: string; info?: string; data: unknown } },
) {
  const data: Record<string, unknown> = {};
  const { value } = cue;
  if (value) {
    if (value.info) {
      let collection = data[value.key];
      if (collection !== Object(collection)) {
        collection = {};
        data[value.key] = collection;
      }
      (collection as Record<string, unknown>)[value.info] = value.data;
    } else {
      data[value.key] = value.data;
    }
  }
  return data;
}

function getChartOptions() {
  return {
    animation: { duration: 0 },
    elements: {
      rectangle: {
        borderWidth: 1,
        borderColor: 'rgba(20, 20, 20, 1)',
      },
    },
    events: ['click', 'touchstart'],
    hover: { mode: null, animationDuration: 0 },
    legend: { display: false },
    maintainAspectRatio: false,
    responsiveAnimationDuration: 0,
    scales: {
      xAxes: [
        {
          id: X_AXIS_SECONDS,
          ticks: {
            beginAtZero: true,
            sampleSize: 0,
            maxRotation: 0,
            callback: (tickValue: number, i: number, ticks: { length: number }[]) => {
              if (i === 0 || i === ticks.length - 1) {
                return tickValue ? '' : '0';
              }
              return hhmmss(tickValue, 2);
            },
          },
        },
      ],
      yAxes: [{ gridLines: { display: false } }],
    },
    tooltips: { enabled: false },
  };
}

function arrayFind<T>(array: T[], predicate: (value: T) => boolean): T | undefined {
  for (let k = 0; k < array.length; k++) {
    if (predicate(array[k])) {
      return array[k];
    }
  }
  return undefined;
}
